export const GAME_FIELDS = ['id', 'plaza_id', 'venue_name', 'timezone', 'starts_at', 'home_team', 'away_team', 'phase'];
export const OPTIONAL_GAME_FIELDS = ['scheduled_date', 'title', 'country_code', 'notification_eligible', 'test_activation_window', 'activation_timing'];
export const isNonTeam = sport => sport === 'kpop' || sport === 'running';
export const MAX_ACTIVATION_SECONDS = 604800;
export function validTiming(value, maximum = MAX_ACTIVATION_SECONDS) {
  return !!value && typeof value === 'object' && !Array.isArray(value)
    && Object.keys(value).length === 2 && Object.keys(value).every(key => ['before_seconds', 'after_seconds'].includes(key))
    && Number.isInteger(value.before_seconds) && value.before_seconds >= 0 && value.before_seconds <= maximum
    && Number.isInteger(value.after_seconds) && value.after_seconds >= 1 && value.after_seconds <= maximum;
}
export const PHASES = ['regular', 'postseason', 'all_star'];
const MAX_ROWS = 10000;
export const STABLE_ID = /^[a-z0-9][a-z0-9_-]{0,79}$/;
const hasControl = value => Array.from(value).some(character => character.codePointAt(0) <= 0x1f || character.codePointAt(0) === 0x7f);
export function utf8Size(value) { return new Blob([value]).size; }
// Parse RFC 4180 quoted fields, including embedded commas/newlines and escaped quotes.
export function parseCsv(text) {
  const rows = []; let row = [], cell = '', quoted = false, closed = false;
  for (let i = 0; i < text.length; i += 1) {
    const c = text[i];
    if (quoted) {
      if (c === '"' && text[i + 1] === '"') { cell += '"'; i += 1; }
      else if (c === '"') { quoted = false; closed = true; }
      else cell += c;
    } else if (c === '"' && !cell && !closed) quoted = true;
    else if (c === ',' || c === '\n' || c === '\r') {
      row.push(cell); cell = ''; closed = false;
      if (c !== ',') { if (row.some(value => value !== '')) rows.push(row); row = []; if (c === '\r' && text[i + 1] === '\n') i += 1; }
    } else { if (closed || c === '"') throw new Error('csv'); cell += c; }
  }
  if (quoted) throw new Error('csv');
  row.push(cell); if (row.some(value => value !== '')) rows.push(row);
  const [headers, ...values] = rows;
  if (!headers || new Set(headers).size !== headers.length || !GAME_FIELDS.every(field => headers.includes(field))) throw new Error('columns');
  if (values.some(value => value.length !== headers.length)) throw new Error('columns');
  return values.map(value => Object.fromEntries(headers.map((header, index) => [header, value[index]])));
}
export function parseImport(text, format) {
  if (utf8Size(text) > 8 * 1024 * 1024) throw new Error('size');
  let games;
  if (format === 'csv') games = parseCsv(text.replace(/^\uFEFF/, ''));
  else { try { games = JSON.parse(text); } catch { throw new Error('json'); } }
  if (!Array.isArray(games) || !games.length || games.length > MAX_ROWS || games.some(game => !game || typeof game !== 'object' || Array.isArray(game))) throw new Error('rows');
  if (games.some(game => Object.prototype.hasOwnProperty.call(game, 'activation_window'))) throw new Error('activationWindow');
  return games.map(game => {
    const value = { ...Object.fromEntries(GAME_FIELDS.map(field => [field, typeof game[field] === 'string' ? game[field].trim() : ''])), ...Object.fromEntries(OPTIONAL_GAME_FIELDS.filter(field => Object.prototype.hasOwnProperty.call(game, field)).map(field => [field, game[field]])) };
    if (Object.prototype.hasOwnProperty.call(game, 'notification_eligible')) {
      const flag = game.notification_eligible;
      if (typeof flag === 'boolean') value.notification_eligible = flag;
      else if (format === 'csv' && ['true', 'false', ''].includes(flag?.trim())) value.notification_eligible = flag.trim() === 'true';
      else throw new Error('notification');
    }
    if (format === 'csv' && Object.prototype.hasOwnProperty.call(game, 'activation_timing')) {
      if (game.activation_timing === '') delete value.activation_timing;
      else { try { value.activation_timing = JSON.parse(game.activation_timing); } catch { throw new Error('activationWindow'); } }
    }
    return value;
  });
}
export function validTimezone(value) {
  if (!value || !value.includes('/')) return false;
  try { new Intl.DateTimeFormat('en', { timeZone: value }).format(0); return true; } catch { return false; }
}
export function validInstant(value) {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?(?:Z|[+-]\d{2}:\d{2})$/.test(value || '')) return false;
  const time = Date.parse(value); if (!Number.isFinite(time)) return false;
  const [, hour, minute, second] = value.match(/T(\d{2}):(\d{2}):(\d{2})/);
  if (Number(hour) > 23 || Number(minute) > 59 || Number(second) > 59 || new Date(time).getUTCFullYear() < 2000 || new Date(time).getUTCFullYear() > 2100) return false;
  // Date.parse normalizes impossible dates (e.g. Feb 30); reject those before conversion.
  const [year, month, day] = value.slice(0, 10).split('-').map(Number);
  return month >= 1 && month <= 12 && day >= 1 && day <= new Date(Date.UTC(year, month, 0)).getUTCDate();
}
export function validateGames(games, plazas, { sport, countries = [], environment = 'TEST' } = {}) {
  const counts = new Map(); games.forEach(game => counts.set(game.id, (counts.get(game.id) || 0) + 1));
  const fixtures = new Map();
  const fixtureKey = game => JSON.stringify([game.plaza_id, validInstant(game.starts_at) ? new Date(game.starts_at).toISOString() : game.starts_at, isNonTeam(sport) ? game.title : game.home_team, isNonTeam(sport) ? game.country_code : game.away_team]);
  games.filter(game => game.plaza_id && validInstant(game.starts_at)).forEach(game => { const key = fixtureKey(game); fixtures.set(key, (fixtures.get(key) || 0) + 1); });
  const known = new Set(plazas.map(plaza => String(plaza.id)));
  return games.map(game => {
    const errors = [];
    if (!STABLE_ID.test(game.id) || counts.get(game.id) > 1 || fixtures.get(fixtureKey(game)) > 1) errors.push('duplicate');
    if (!game.plaza_id || !known.has(game.plaza_id)) errors.push('unlinked');
    if (!validInstant(game.starts_at)) errors.push('instant');
    if (!validTimezone(game.timezone)) errors.push('timezone');
    if (game.scheduled_date && (!/^\d{4}-\d{2}-\d{2}$/.test(game.scheduled_date) || !validInstant(`${game.scheduled_date}T00:00:00Z`))) errors.push('scheduledDate');
    if ([game.venue_name, game.home_team, game.away_team].some(value => value && (value.trim() !== value || hasControl(value) || utf8Size(value) > 240)) || (game.home_team && game.home_team === game.away_team)) errors.push('required');
    if (isNonTeam(sport)) {
      if (typeof game.title !== 'string' || !game.title.trim() || game.title !== game.title.trim() || hasControl(game.title) || utf8Size(game.title) > 240 || !/^[A-Z]{2}$/.test(game.country_code || '') || !countries.includes(game.country_code) || game.notification_eligible !== false) errors.push('event');
      const plaza = plazas.find(item => String(item.id) === game.plaza_id);
      if (plaza && (plaza.category !== sport || (plaza.country_code && plaza.country_code !== game.country_code))) errors.push('category');
    }
    if (game.activation_timing !== undefined && (!isNonTeam(sport) || game.notification_eligible !== false || !validTiming(game.activation_timing) || game.test_activation_window !== undefined)) errors.push('activationWindow');
    if (game.test_activation_window !== undefined) {
      const window = game.test_activation_window;
      if (environment !== 'TEST' || !isNonTeam(sport) || !game.title?.startsWith('[TEST]') || game.notification_eligible !== false || !window || typeof window !== 'object' || Array.isArray(window) || Object.keys(window).some(key => !['before_seconds', 'after_seconds'].includes(key)) || !Number.isInteger(window.before_seconds) || window.before_seconds < 0 || window.before_seconds > 300 || !Number.isInteger(window.after_seconds) || window.after_seconds < 1 || window.after_seconds > 300) errors.push('activationWindow');
    }
    if (game.activation_window !== undefined) errors.push('activationWindow');
    if (game.phase && !PHASES.includes(game.phase)) errors.push('phase');
    return errors;
  });
}
export function normalizedGames(games) {
  return games.map(game => ({ ...game, starts_at: validInstant(game.starts_at) ? new Date(game.starts_at).toISOString() : game.starts_at }));
}
export function localTime(instant, timezone, locale) {
  if (!validInstant(instant) || !validTimezone(timezone)) return '—';
  return new Intl.DateTimeFormat(locale, { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', timeZoneName: 'short', hour12: false }).format(new Date(instant));
}
export function activeWindow(game, sport) {
  if (!validInstant(game.starts_at)) return null;
  if (game.activation_window && validInstant(game.activation_window.active_from) && validInstant(game.activation_window.active_until)) return [game.activation_window.active_from, game.activation_window.active_until];
  const start = Date.parse(game.starts_at);
  if (isNonTeam(sport)) {
    if (game.activation_timing !== undefined && game.test_activation_window !== undefined) return null;
    const window = game.activation_timing || game.test_activation_window;
    return validTiming(window, game.activation_timing ? MAX_ACTIVATION_SECONDS : 300) ? [new Date(start - window.before_seconds * 1000).toISOString(), new Date(start + window.after_seconds * 1000).toISOString()] : null;
  }
  return [new Date(start - 120 * 60000).toISOString(), new Date(start + 300 * 60000).toISOString()];
}

export function draftErrors(game, errors) {
  return errors.filter(error => error !== 'unlinked' && !(error === 'instant' && !game.starts_at) && !(error === 'timezone' && !game.timezone));
}

export function venueOffset(game) {
  if (!validInstant(game.starts_at) || !validTimezone(game.timezone)) return '';
  return new Intl.DateTimeFormat('en', { timeZone: game.timezone, timeZoneName: 'longOffset' }).formatToParts(new Date(game.starts_at)).find(part => part.type === 'timeZoneName')?.value.replace('GMT', 'UTC') || '';
}
